import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { keepPreviousData, useQuery } from '@tanstack/react-query'
import {
  Archive,
  Bookmark,
  ChevronLeft,
  ChevronRight,
  EyeOff,
  FolderOpen,
  Lock,
  Search,
  ShieldCheck,
  X,
} from 'lucide-react'
import clsx from 'clsx'
import { dbConsoleService, type ConsoleSearchHit } from '@/services/dbConsole'
import { count } from './format'
import {
  archives,
  archivedPhrase,
  dealFileBlocker,
  environmentNote,
  lifecycleSummary,
  openDealFile,
  rowArchived,
} from './lifecycle'
import { RowDetailDrawer } from './RowDetailDrawer'
import { TableExportDialog } from './TableExportDialog'

const PAGE_SIZE = 50

type FocusedCell = {
  table: string
  column: string
  rowKey: Record<string, string | number | boolean> | null
}

function cell(value: unknown): string {
  if (value === null || value === undefined) return '∅'
  if (value === '') return '""'
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function rowMatchesKey(
  row: Record<string, unknown>,
  rowKey: Record<string, string | number | boolean>,
): boolean {
  return Object.entries(rowKey).every(([name, value]) => String(row[name]) === String(value))
}

/** Filters change as fast as someone types; the query should not. */
function useDebounced<T>(value: T, delay = 220): T {
  const [debounced, setDebounced] = useState(value)
  useEffect(() => {
    const timer = setTimeout(() => setDebounced(value), delay)
    return () => clearTimeout(timer)
  }, [value, delay])
  return debounced
}

function hitKind(hit: ConsoleSearchHit): string {
  if (hit.kind === 'field') return 'Catalog field'
  if (hit.kind === 'column') return 'Field'
  if (hit.kind === 'cell') return 'Stored value'
  return 'Table'
}

/** "1 of 3 live deals hold it · 4 of 5 archived rows do" — never one mixed figure. */
function holdersLine(hit: ConsoleSearchHit): string | null {
  const holders = hit.holders
  if (!holders) return null
  const live = `${count(holders.liveRows)} of ${count(holders.liveScanned)} live deals hold it`
  const archived =
    holders.archivedRows !== null && holders.archivedScanned
      ? ` · ${count(holders.archivedRows)} of ${count(holders.archivedScanned)} archived rows do`
      : ''
  return `${live}${archived}${holders.complete ? '' : ' (newest rows read)'}`
}

/** A search hit names a deal whose file will open for this account. */
function hitOpensDeal(hit: ConsoleSearchHit, viewerEnvironment: string | undefined): string | null {
  if (hit.table !== 'loans' || !hit.rowKey || hit.archived) return null
  const id = hit.rowKey.id
  if (typeof id !== 'string') return null
  if (viewerEnvironment && hit.environment && hit.environment !== viewerEnvironment) return null
  return id
}

/**
 * Raw table access — the "look under the hood" view.
 *
 * The first search spans every governed console table and returns a primary-key
 * coordinate for each stored-value hit. Selecting one reopens that exact row,
 * scrolls the matching cell into view, and leaves a visible focus ring. The
 * second search remains a fast filter within the selected table.
 */
export function TableBrowser() {
  const [selected, setSelected] = useState('')
  const [page, setPage] = useState(1)
  const [tableFilter, setTableFilter] = useState('')
  const [globalSearch, setGlobalSearch] = useState('')
  const [rowSearch, setRowSearch] = useState('')
  const [focus, setFocus] = useState<FocusedCell | null>(null)
  // Archived rows (soft-deleted; for loans, mostly the test-data reset's) are
  // listed only on request, and the header always says how many there are.
  const [includeArchived, setIncludeArchived] = useState(false)
  const [detailRow, setDetailRow] = useState<Record<string, unknown> | null>(null)
  const [exportOpen, setExportOpen] = useState(false)
  const debouncedGlobalSearch = useDebounced(globalSearch)
  const debouncedRowSearch = useDebounced(rowSearch)
  const focusedCellRef = useRef<HTMLTableCellElement>(null)
  const focusedHeaderRef = useRef<HTMLTableCellElement>(null)

  const inventory = useQuery({
    queryKey: ['db-console', 'tables'],
    queryFn: dbConsoleService.tables,
    staleTime: 30_000,
  })

  // Q9: open the first server-ranked calculation table, not an arbitrary
  // alphabetic table. Fall back safely when an older API has no bookmarks.
  useEffect(() => {
    if (selected || !inventory.data) return
    const readableTables = inventory.data.tables.filter(
      (table) => table.present !== false && table.rowAccess !== false,
    )
    const firstCalculation = readableTables
      .filter((table) => table.bookmarked)
      .sort((a, b) => (a.bookmarkRank ?? Number.MAX_SAFE_INTEGER) - (b.bookmarkRank ?? Number.MAX_SAFE_INTEGER))[0]
    setSelected(firstCalculation?.table ?? readableTables[0]?.table ?? inventory.data.tables[0]?.table ?? '')
  }, [inventory.data, selected])

  const active = inventory.data?.tables.find((table) => table.table === selected)
  const readable = active?.rowAccess !== false && active?.present !== false

  const visibleTables = useMemo(() => {
    const list = inventory.data?.tables ?? []
    const query = tableFilter.trim().toLowerCase()
    if (!query) return list
    return list.filter(
      (table) =>
        table.table.toLowerCase().includes(query) ||
        table.label.toLowerCase().includes(query),
    )
  }, [inventory.data?.tables, tableFilter])

  const tableGroups = useMemo(() => {
    const calculations = visibleTables
      .filter((table) => table.bookmarked)
      .sort((a, b) => (a.bookmarkRank ?? Number.MAX_SAFE_INTEGER) - (b.bookmarkRank ?? Number.MAX_SAFE_INTEGER))
    const others = visibleTables.filter((table) => !table.bookmarked)
    return [
      { id: 'calculations', label: 'Calculation tables', tables: calculations },
      { id: 'all', label: 'All other tables', tables: others },
    ].filter((group) => group.tables.length > 0)
  }, [visibleTables])

  const globalResults = useQuery({
    queryKey: ['db-console', 'search', debouncedGlobalSearch.trim()],
    queryFn: ({ signal }) => dbConsoleService.search(debouncedGlobalSearch.trim(), signal),
    enabled: debouncedGlobalSearch.trim().length >= 2,
    retry: false,
  })

  useEffect(() => {
    setPage(1)
  }, [debouncedRowSearch, selected, focus, includeArchived])

  useEffect(() => {
    setDetailRow(null)
  }, [selected])

  const focusedRowKey = focus?.table === selected ? focus.rowKey : null
  const rows = useQuery({
    queryKey: ['db-console', 'rows', selected, page, debouncedRowSearch, focusedRowKey, includeArchived],
    queryFn: () =>
      dbConsoleService.tableRows(
        selected,
        page,
        PAGE_SIZE,
        focusedRowKey ? undefined : debouncedRowSearch,
        focusedRowKey,
        includeArchived,
      ),
    enabled: Boolean(selected) && readable,
    placeholderData: keepPreviousData,
  })

  // A page kept on screen while the next loads may belong to the previous
  // table; its counts and columns must not be read as this table's.
  const data = rows.data?.table === selected ? rows.data : undefined
  const searching = Boolean(debouncedRowSearch.trim()) && !focusedRowKey
  const tableTotal = data?.tableTotal ?? data?.total
  const lifecycle = data?.lifecycle ?? active?.lifecycle ?? null
  const scopeLifecycle = (searching ? data?.matchLifecycle : null) ?? lifecycle
  const archivable = archives(lifecycle)
  const archivedInScope = archivable && scopeLifecycle ? scopeLifecycle.archivedRows : 0
  const environment = environmentNote(lifecycle)
  // Describe the rows on screen, not the toggle: while the next page loads the
  // previous one is still shown, and its labels must still be true of it.
  const showingArchived = data?.includeArchived ?? includeArchived

  /** Double-click or Open: a deal opens in its deal file; anything else — or
   *  a deal whose file will not open for this account — opens every column. */
  const openRow = useCallback(
    (row: Record<string, unknown>) => {
      if (selected === 'loans' && typeof row.id === 'string') {
        const blocker = dealFileBlocker(row, rowArchived(row, lifecycle), data?.viewerEnvironment)
        if (!blocker) {
          openDealFile(row.id)
          return
        }
      }
      setDetailRow(row)
    },
    [selected, lifecycle, data?.viewerEnvironment],
  )

  useEffect(() => {
    if (!focus || focus.table !== selected || !data) return
    const target = focus.rowKey ? focusedCellRef.current : focusedHeaderRef.current
    if (!target) return
    target.scrollIntoView({ block: 'center', inline: 'center', behavior: 'smooth' })
    target.focus({ preventScroll: true })
  }, [data, focus, selected])

  const openHit = (hit: ConsoleSearchHit) => {
    setSelected(hit.table)
    setTableFilter('')
    setRowSearch('')
    setIncludeArchived(false)
    setPage(1)
    setFocus(
      hit.column
        ? { table: hit.table, column: hit.column, rowKey: hit.rowKey }
        : null,
    )
    setGlobalSearch('')
  }

  const chooseTable = (table: string) => {
    setSelected(table)
    setRowSearch('')
    setFocus(null)
    setIncludeArchived(false)
    setPage(1)
  }

  const showGlobalResults = globalSearch.trim().length >= 2

  return (
    <div className="flex min-h-0 flex-1 flex-col gap-2">
      <div className="relative z-30 flex-shrink-0 rounded-lg border border-amber-300/15 bg-[#0b0b0b] p-2">
        <div className="relative">
          <Search
            size={14}
            className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-amber-300/70"
          />
          <input
            type="search"
            value={globalSearch}
            onChange={(event) => setGlobalSearch(event.target.value)}
            placeholder="Search every table, field, and stored value — e.g. LTV or a deal value"
            aria-label="Search across the database console"
            aria-expanded={showGlobalResults}
            aria-controls="database-console-search-results"
            className="w-full rounded-lg border border-amber-300/20 bg-black/50 py-2 pl-9 pr-9 text-[12px] text-stone-100 placeholder:text-stone-600 focus:border-amber-300/45 focus:outline-none"
          />
          {globalSearch && (
            <button
              type="button"
              onClick={() => setGlobalSearch('')}
              aria-label="Clear console search"
              className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-stone-500 hover:text-stone-200"
            >
              <X size={13} />
            </button>
          )}
        </div>

        {showGlobalResults && (
          <div
            id="database-console-search-results"
            role="listbox"
            aria-label="Database console search results"
            className="absolute left-2 right-2 top-[calc(100%-2px)] max-h-[330px] overflow-y-auto rounded-b-lg border border-amber-300/20 bg-[#10100f] shadow-2xl"
          >
            {globalResults.isFetching && !globalResults.data ? (
              <p className="px-3 py-3 text-[11px] text-amber-200/70">Searching the governed database…</p>
            ) : globalResults.isError ? (
              <p className="px-3 py-3 text-[11px] text-red-300">The console-wide search could not be completed.</p>
            ) : (globalResults.data?.results.length ?? 0) === 0 ? (
              <p className="px-3 py-3 text-[11px] text-stone-500">No table, field, or stored value matches.</p>
            ) : (
              <>
                {globalResults.data?.results.map((hit, index) => {
                  const dealId = hitOpensDeal(hit, globalResults.data?.viewerEnvironment)
                  const holders = holdersLine(hit)
                  return (
                    <div
                      key={`${hit.kind}:${hit.table}:${hit.column ?? ''}:${JSON.stringify(hit.rowKey)}:${index}`}
                      className="flex items-stretch border-b border-white/[0.04]"
                    >
                      <button
                        type="button"
                        role="option"
                        aria-selected="false"
                        onClick={() => openHit(hit)}
                        className="flex min-w-0 flex-1 items-start gap-2 px-3 py-2 text-left hover:bg-amber-400/[0.07]"
                      >
                        {hit.bookmarked ? (
                          <Bookmark size={11} className="mt-0.5 flex-shrink-0 fill-amber-300/30 text-amber-300" />
                        ) : (
                          <Search size={11} className="mt-0.5 flex-shrink-0 text-stone-600" />
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="flex flex-wrap items-baseline gap-x-2">
                            <span className="text-[11px] font-medium text-stone-100">{hit.label}</span>
                            <span className="font-mono text-[9px] text-stone-600">{hit.table}</span>
                            {hit.archived && <ArchivedPill />}
                          </span>
                          <span className="mt-0.5 block truncate text-[10px] text-stone-400">
                            <span className="mr-1.5 uppercase tracking-[0.1em] text-amber-300/65">{hitKind(hit)}</span>
                            {hit.column && hit.kind !== 'field' && <span className="font-mono text-stone-300">{hit.column}</span>}
                            {hit.displayValue !== null && hit.kind !== 'table' && (
                              <span className="ml-2 font-mono text-stone-500">{hit.displayValue}</span>
                            )}
                          </span>
                          {holders && (
                            <span className="mt-0.5 block truncate text-[10px] text-stone-500">{holders}</span>
                          )}
                        </span>
                      </button>
                      {dealId && (
                        <button
                          type="button"
                          onClick={() => {
                            setGlobalSearch('')
                            openDealFile(dealId)
                          }}
                          aria-label={`Open deal file ${dealId}`}
                          title="Open this deal's file"
                          className="flex flex-shrink-0 items-center gap-1 border-l border-white/[0.04] px-3 text-[10px] text-amber-300/80 transition hover:bg-amber-400/[0.07] hover:text-amber-100"
                        >
                          <FolderOpen size={11} /> Open deal
                        </button>
                      )}
                    </div>
                  )
                })}
                {(globalResults.data?.truncated || (globalResults.data?.skippedTables.length ?? 0) > 0) && (
                  <p className="px-3 py-2 text-[9px] text-stone-600">
                    {globalResults.data?.truncated ? 'More matches exist; narrow the search. ' : ''}
                    {(globalResults.data?.skippedTables.length ?? 0) > 0
                      ? `${globalResults.data?.skippedTables.length} unavailable table(s) were skipped.`
                      : ''}
                  </p>
                )}
              </>
            )}
          </div>
        )}
      </div>

      <div className="flex min-h-0 flex-1 gap-2">
        <aside
          aria-label="Database tables"
          className="flex w-[250px] flex-shrink-0 flex-col overflow-hidden rounded-lg border border-amber-300/10 bg-[#0b0b0b]"
        >
          <div className="border-b border-amber-300/10 px-3 py-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-amber-200/80">
            Tables
          </div>
          <div className="relative border-b border-amber-300/10 px-2 py-1.5">
            <Search
              size={11}
              className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-stone-600"
            />
            <input
              type="search"
              value={tableFilter}
              onChange={(event) => setTableFilter(event.target.value)}
              placeholder="Filter table names…"
              aria-label="Filter table list"
              className="w-full rounded-md border border-amber-300/10 bg-black/40 py-1 pl-7 pr-2 text-[11px] text-stone-200 placeholder:text-stone-600 focus:border-amber-300/30 focus:outline-none"
            />
          </div>
          <div className="min-h-0 flex-1 overflow-y-auto py-1">
            {visibleTables.length === 0 && (
              <p className="px-3 py-2 text-[10px] text-stone-600">No tables match.</p>
            )}
            {tableGroups.map((group) => (
              <section key={group.id} aria-label={group.label}>
                <div className="sticky top-0 z-10 flex items-center gap-1.5 border-y border-amber-300/[0.07] bg-[#0e0e0e] px-3 py-1 text-[8px] font-semibold uppercase tracking-[0.14em] text-stone-600">
                  {group.id === 'calculations' && (
                    <Bookmark size={8} className="fill-amber-300/25 text-amber-300/70" />
                  )}
                  {group.label}
                </div>
                <ul>
                  {group.tables.map((table) => (
                    <li key={table.table}>
                      <button
                        type="button"
                        onClick={() => chooseTable(table.table)}
                        className={clsx(
                          'flex w-full items-center gap-1.5 px-3 py-1 text-left transition',
                          selected === table.table
                            ? 'bg-amber-400/[0.09] text-stone-100'
                            : 'text-stone-400 hover:bg-white/[0.02]',
                        )}
                      >
                        {table.bookmarked && (
                          <Bookmark size={9} className="flex-shrink-0 fill-amber-300/25 text-amber-300/80" />
                        )}
                        <span className="min-w-0 flex-1">
                          <span className="block truncate text-[11px]">{table.label}</span>
                          <span className="block truncate font-mono text-[9px] text-stone-600">
                            {table.table}
                          </span>
                        </span>
                        {!table.rowAccess && <Lock size={9} className="flex-shrink-0 text-amber-400/60" />}
                        {archives(table.lifecycle) ? (
                          <span
                            className="flex flex-shrink-0 flex-col items-end font-mono text-[9px] leading-tight"
                            title={`${lifecycleSummary(table.lifecycle)} · ${count(table.rows ?? 0)} stored`}
                          >
                            <span className="text-stone-500">{count(table.lifecycle.liveRows)} live</span>
                            {table.lifecycle.archivedRows > 0 && (
                              <span className="text-stone-700">+{count(table.lifecycle.archivedRows)} archived</span>
                            )}
                          </span>
                        ) : (
                          <span className="flex-shrink-0 font-mono text-[9px] text-stone-500">
                            {table.rows === null ? '—' : count(table.rows)}
                          </span>
                        )}
                      </button>
                    </li>
                  ))}
                </ul>
              </section>
            ))}
          </div>
        </aside>

        <div className="flex min-w-0 flex-1 flex-col gap-2">
          <div className="flex flex-shrink-0 flex-col gap-2 rounded-lg border border-amber-300/10 bg-[#0b0b0b] px-3 py-2">
            <div className="flex flex-wrap items-baseline gap-2">
              <span className="text-[12px] font-medium text-stone-100">{active?.label ?? selected}</span>
              <span className="font-mono text-[10px] text-stone-600">{selected}</span>
              {data && (
                <span className="text-[10px] text-stone-500" data-testid="table-count">
                  {focusedRowKey ? (
                    <>
                      Exact row · {count(tableTotal ?? 0)} stored rows
                      {archivable && lifecycle ? ` (${lifecycleSummary(lifecycle)})` : ''}
                    </>
                  ) : searching && archivable && scopeLifecycle ? (
                    <>
                      {count(scopeLifecycle.liveRows)} live match{scopeLifecycle.liveRows === 1 ? '' : 'es'} ·{' '}
                      {count(scopeLifecycle.archivedRows)} archived · of {count(tableTotal ?? 0)} stored rows
                    </>
                  ) : searching ? (
                    <>
                      {count(data.total)} match{data.total === 1 ? '' : 'es'} of {count(tableTotal ?? 0)} · ordered by{' '}
                      {data.orderedBy}
                    </>
                  ) : archivable && lifecycle ? (
                    <>
                      <span className="text-stone-200">{lifecycleSummary(lifecycle)}</span> · ordered by{' '}
                      {data.orderedBy} (newest first)
                    </>
                  ) : (
                    <>
                      {count(data.total)} rows · ordered by {data.orderedBy} (newest first)
                    </>
                  )}
                </span>
              )}
              {readable && selected && (
                <button
                  type="button"
                  onClick={() => setExportOpen(true)}
                  title="Raise a two-person export request for this table"
                  className={clsx(
                    'flex items-center gap-1 rounded border border-amber-300/20 px-2 py-0.5 text-[10px] text-amber-100 transition hover:bg-amber-400/[0.1]',
                    !(active && active.columns.some((column) => column.redacted)) && 'ml-auto',
                  )}
                >
                  <ShieldCheck size={10} /> Export this table
                </button>
              )}
              {active && active.columns.some((column) => column.redacted) && (
                <span className="ml-auto flex items-center gap-1 text-[10px] text-amber-300/80">
                  <EyeOff size={10} />
                  {active.columns.filter((column) => column.redacted).length} column(s) redacted:{' '}
                  {active.columns
                    .filter((column) => column.redacted)
                    .map((column) => column.name)
                    .join(', ')}
                </span>
              )}
            </div>

            {readable && data && archivable && !focusedRowKey && (
              <div className="flex flex-wrap items-center gap-2 text-[10px]">
                <button
                  type="button"
                  role="switch"
                  aria-checked={includeArchived}
                  onClick={() => setIncludeArchived((previous) => !previous)}
                  disabled={!includeArchived && archivedInScope === 0}
                  className={clsx(
                    'flex items-center gap-1 rounded border px-2 py-0.5 transition disabled:opacity-40',
                    includeArchived
                      ? 'border-amber-300/35 bg-amber-400/[0.12] text-amber-100'
                      : 'border-amber-300/15 text-stone-300 enabled:hover:border-amber-300/30',
                  )}
                >
                  <Archive size={10} />
                  Include archived ({count(archivedInScope)})
                </button>
                <span className="text-stone-500" data-testid="archive-scope">
                  {showingArchived
                    ? 'Showing live and archived rows; archived rows are marked.'
                    : archivedInScope > 0 && scopeLifecycle
                      ? `Showing live rows only; ${archivedPhrase(scopeLifecycle)} kept and not listed.`
                      : 'Showing live rows. No archived rows here.'}
                </span>
                {environment && <span className="text-stone-600">{environment}</span>}
              </div>
            )}

            {readable && (
              <div className="relative">
                <Search
                  size={13}
                  className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-stone-500"
                />
                <input
                  type="search"
                  value={rowSearch}
                  onChange={(event) => {
                    setRowSearch(event.target.value)
                    setFocus(null)
                  }}
                  placeholder='Filter this table — phrase, id, or column:"value"'
                  aria-label="Search database rows in the selected table"
                  className="w-full rounded-lg border border-amber-300/15 bg-black/40 py-1.5 pl-8 pr-9 text-[12px] text-stone-100 placeholder:text-stone-600 focus:border-amber-300/35 focus:outline-none"
                />
                {(rowSearch || focus) && (
                  <button
                    type="button"
                    onClick={() => {
                      setRowSearch('')
                      setFocus(null)
                    }}
                    aria-label="Clear row search and focused cell"
                    className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-0.5 text-stone-500 hover:text-stone-200"
                  >
                    <X size={13} />
                  </button>
                )}
                {rows.isFetching && (searching || focusedRowKey) && (
                  <span className="pointer-events-none absolute right-9 top-1/2 -translate-y-1/2 font-mono text-[9px] text-amber-300/70">
                    searching…
                  </span>
                )}
              </div>
            )}
          </div>

          <div className="min-h-0 flex-1 overflow-auto rounded-lg border border-amber-300/10 bg-[#0c0c0c]">
            {!readable ? (
              <div className="flex h-full flex-col items-center justify-center gap-2 px-6 text-center">
                <Lock className="h-5 w-5 text-amber-400/70" />
                <p className="text-[12px] text-stone-200">Contents sealed</p>
                <p className="max-w-md text-[11px] leading-relaxed text-stone-500">
                  {active?.rowAccessNote ??
                    'This table is defined by the application but not present in the database.'}{' '}
                  Its row count is still reported, so you can confirm it exists and how much it holds.
                </p>
              </div>
            ) : rows.isError ? (
              <p className="p-4 text-[11px] text-red-300">This table could not be read.</p>
            ) : !data ? (
              <p className="p-4 text-[11px] text-stone-500">Loading rows…</p>
            ) : data.rows.length === 0 ? (
              <p className="p-4 text-[11px] text-stone-500">
                {focusedRowKey
                  ? 'The focused row no longer exists.'
                  : !showingArchived && archivedInScope > 0
                    ? `No live rows${searching ? ' match this search' : ''}. ${count(archivedInScope)} archived row${archivedInScope === 1 ? '' : 's'} ${searching ? (archivedInScope === 1 ? 'does' : 'do') : (archivedInScope === 1 ? 'is' : 'are')} kept — turn on Include archived to list ${archivedInScope === 1 ? 'it' : 'them'}.`
                    : searching
                      ? 'No stored rows match this search in the selected table.'
                      : 'This table is present and readable, and currently holds no rows.'}
              </p>
            ) : (
              <table className="w-full border-collapse text-left" style={{ minWidth: 'max-content' }}>
                <thead className="sticky top-0 z-20 bg-[#121212]">
                  <tr>
                    <th scope="col" className="sticky left-0 z-10 bg-[#121212] px-2 py-2 text-[9px] font-medium uppercase tracking-[0.1em] text-stone-600">
                      <span className="sr-only">Open</span>
                    </th>
                    {data.columns.map((column) => {
                      const focusedHeader = focus?.table === selected && focus.column === column.name && !focus.rowKey
                      return (
                        <th
                          key={column.name}
                          ref={focusedHeader ? focusedHeaderRef : undefined}
                          tabIndex={focusedHeader ? -1 : undefined}
                          scope="col"
                          className={clsx(
                            'whitespace-nowrap px-2.5 py-2 text-[9px] font-medium uppercase tracking-[0.1em] text-stone-500',
                            focusedHeader && 'bg-amber-400/20 text-amber-100 ring-1 ring-inset ring-amber-300',
                          )}
                          title={column.type}
                        >
                          {column.name}
                          {column.redacted && (
                            <EyeOff size={8} className="ml-1 inline text-amber-400/60" />
                          )}
                        </th>
                      )
                    })}
                  </tr>
                </thead>
                <tbody>
                  {data.rows.map((row, index) => {
                    const primary = data.columns.filter((column) => column.primaryKey)
                    const fallback = data.columns[0]
                    const key = primary.length > 0
                      ? primary.map((column) => String(row[column.name])).join(':')
                      : String(row[fallback.name] ?? index)
                    const archived = rowArchived(row, lifecycle)
                    const opensDeal =
                      selected === 'loans' &&
                      typeof row.id === 'string' &&
                      !dealFileBlocker(row, archived, data.viewerEnvironment)
                    return (
                      <tr
                        key={key}
                        onDoubleClick={() => openRow(row)}
                        data-archived={archived ? 'true' : undefined}
                        title={opensDeal ? 'Double-click to open the deal file' : 'Double-click to open every column'}
                        className={clsx(
                          'cursor-default border-b border-white/[0.04] text-[11px] hover:bg-amber-400/[0.04]',
                          index % 2 === 1 && 'bg-white/[0.012]',
                          archived && 'opacity-60',
                          detailRow === row && 'bg-amber-400/[0.08]',
                        )}
                      >
                        <td className="sticky left-0 z-[1] whitespace-nowrap bg-[#0c0c0c] px-2 py-1">
                          <span className="flex items-center gap-1">
                            <button
                              type="button"
                              onClick={() => openRow(row)}
                              aria-label={`${opensDeal ? 'Open deal file' : 'Open row'} ${key}`}
                              className="flex items-center gap-1 rounded border border-amber-300/15 px-1.5 py-0.5 text-[9px] text-amber-200/90 transition hover:border-amber-300/40 hover:text-amber-100"
                            >
                              <FolderOpen size={9} /> Open
                            </button>
                            {archived && <ArchivedPill />}
                          </span>
                        </td>
                        {data.columns.map((column) => {
                          const value = row[column.name]
                          const focusedCell = Boolean(
                            focus?.table === selected &&
                            focus.column === column.name &&
                            focus.rowKey &&
                            rowMatchesKey(row, focus.rowKey),
                          )
                          return (
                            <td
                              key={column.name}
                              ref={focusedCell ? focusedCellRef : undefined}
                              tabIndex={focusedCell ? -1 : undefined}
                              aria-current={focusedCell ? 'true' : undefined}
                              data-focused-cell={focusedCell ? 'true' : undefined}
                              className={clsx(
                                'max-w-[280px] truncate px-2.5 py-1.5 font-mono text-[10px]',
                                focusedCell && 'bg-amber-400/20 text-amber-50 ring-1 ring-inset ring-amber-300',
                                !focusedCell && value === '[redacted]'
                                  ? 'text-amber-300/70'
                                  : !focusedCell && (value === null || value === undefined)
                                    ? 'text-stone-700'
                                    : !focusedCell && 'text-stone-200',
                              )}
                              title={cell(value)}
                            >
                              {cell(value)}
                            </td>
                          )
                        })}
                      </tr>
                    )
                  })}
                </tbody>
              </table>
            )}
          </div>

          {readable && data && !focusedRowKey && (
            <div className="flex flex-shrink-0 items-center justify-between px-1 text-[10px] text-stone-500">
              <span>
                {data.rows.length === 0
                  ? searching
                    ? '0 matches'
                    : '0 rows'
                  : (
                    <>
                      Showing {count((data.page - 1) * data.pageSize + 1)}–
                      {count((data.page - 1) * data.pageSize + data.rows.length)} of{' '}
                      {count(data.total)}
                      {archivable ? (showingArchived ? ' live and archived' : ' live') : ''}
                      {searching ? ' matches' : archivable ? ' rows' : ''}
                      <span className="ml-2 text-stone-600">
                        Double-click a row, or press Open, to open it
                        {selected === 'loans' ? ' as its deal file' : ''}
                      </span>
                    </>
                  )}
              </span>
              <span className="flex items-center gap-1">
                <button
                  type="button"
                  onClick={() => setPage((previous) => Math.max(1, previous - 1))}
                  disabled={data.page <= 1}
                  aria-label="Previous page"
                  className="rounded border border-amber-300/10 p-1 transition enabled:hover:border-amber-300/30 disabled:opacity-30"
                >
                  <ChevronLeft size={12} />
                </button>
                <span className="font-mono">
                  {data.page} / {Math.max(1, data.pageCount)}
                </span>
                <button
                  type="button"
                  onClick={() => setPage((previous) => previous + 1)}
                  disabled={data.page >= data.pageCount}
                  aria-label="Next page"
                  className="rounded border border-amber-300/10 p-1 transition enabled:hover:border-amber-300/30 disabled:opacity-30"
                >
                  <ChevronRight size={12} />
                </button>
              </span>
            </div>
          )}
        </div>

        {detailRow && data && (
          <RowDetailDrawer
            tableLabel={active?.label ?? selected}
            table={selected}
            columns={data.columns}
            row={detailRow}
            archived={rowArchived(detailRow, lifecycle)}
            {...rowDetailContext(selected, detailRow, rowArchived(detailRow, lifecycle), data.viewerEnvironment)}
            onClose={() => setDetailRow(null)}
          />
        )}
      </div>

      {exportOpen && selected && (
        <TableExportDialog
          table={selected}
          label={active?.label ?? selected}
          rows={active?.rows ?? data?.tableTotal ?? null}
          lifecycle={lifecycle}
          onClose={() => setExportOpen(false)}
        />
      )}
    </div>
  )
}

function ArchivedPill() {
  return (
    <span className="inline-flex items-center gap-0.5 rounded bg-stone-500/15 px-1 py-px text-[8px] uppercase tracking-wider text-stone-300 ring-1 ring-inset ring-stone-400/25">
      <Archive size={7} /> Archived
    </span>
  )
}

/** What the stored-row drawer says, and offers, for a row of `table`. */
function rowDetailContext(
  table: string,
  row: Record<string, unknown>,
  archived: boolean,
  viewerEnvironment: string | undefined,
): { note: string | null; action: { label: string; onClick: () => void } | null } {
  if (table === 'loans' && typeof row.id === 'string') {
    const blocker = dealFileBlocker(row, archived, viewerEnvironment)
    const id = row.id
    return {
      note: blocker,
      action: blocker ? null : { label: 'Open deal file', onClick: () => openDealFile(id) },
    }
  }
  if (table === 'deal_ingestions') {
    const loanId = row.converted_to_loan_id
    const blocked = dealFileBlocker(row, archived, viewerEnvironment)
    const opens = typeof loanId === 'string' && loanId !== '' && !blocked
    return {
      note: archived
        ? 'This intake package is archived. It is kept, not deleted; every stored column is shown here.'
        : 'Intake packages have no screen of their own to open, so every stored column is shown here.',
      action: opens ? { label: `Open the deal it created (${loanId})`, onClick: () => openDealFile(loanId) } : null,
    }
  }
  return {
    note: archived ? 'This row is archived. It is kept, not deleted.' : null,
    action: null,
  }
}
